/**
 * `PUT /me/sessions/:id/feedback` as a mutation that survives the app being
 * closed offline (T55, plan D18).
 *
 * ── WHY IT MUST BE A REGISTERED DEFAULT ────────────────────────────────────
 * Offline, TanStack pauses the mutation and the persister stores its key and
 * variables — never its function. On the next launch the cache is restored
 * and TanStack looks the function up by `mutationKey` in
 * `setMutationDefaults` AT RESTORE TIME. A default registered later (this
 * module is loaded with the workout screen, after the restore) is too late,
 * and the restored mutation can never run. `registerFeedbackMutationDefaults`
 * is therefore called from `data/mutations.ts`, which `provider.tsx` runs
 * before the persister restores.
 *
 * ── PUT REPLACES, SO A REPLAY IS HARMLESS ──────────────────────────────────
 * One feedback row per session, last write wins (`session-feedback.ts`).
 * Retrying after an unseen success writes the same row again.
 *
 * ── ORDERED AFTER THE SESSION ──────────────────────────────────────────────
 * The session id is the client UUIDv7, the same id the server stores (D19),
 * so the form can be sent before the outbox has delivered the start. Until
 * it has, the server answers 404 `workout.session_not_found`; that answer is
 * retried with a backoff like a network error, which puts the feedback
 * behind the outbox's start without coupling the two queues. Nothing here
 * ends the session: the store's one queued end is the only caller (D116).
 */

import { ApiError, SessionExpiredError } from "@repo/client/api";
import type { AuthorizedFetcher } from "@repo/client/data/core";
import { sessionFeedbackMutation } from "@repo/client/data/workout";
import type { SessionFeedbackInput } from "@repo/contract/training";
import type { QueryClient } from "@tanstack/react-query";

export type FeedbackVariables = SessionFeedbackInput & {
  readonly sessionId: string;
};

/** T19's key (`sessionFeedbackMutation`), so the registry and the hook agree. */
export const FEEDBACK_MUTATION_KEY = [
  "workout",
  "sessions",
  "feedback",
] as const;

/** Enough to outlast a start that is still backing off in the outbox. */
export const FEEDBACK_MAX_RETRIES = 8;

const SESSION_NOT_FOUND = "workout.session_not_found";
const MAX_DELAY_MS = 60_000;

/**
 * Retry what time can fix: the network, a 5xx, a 429, and the 404 of a
 * session whose start has not arrived yet. A 4xx that is the request's fault
 * (a 422, someone else's session answering 404 forever) stops at the cap.
 * An expired session never retries — the sign-in flow owns that.
 */
export function shouldRetryFeedback(
  failureCount: number,
  error: unknown
): boolean {
  if (failureCount >= FEEDBACK_MAX_RETRIES) {
    return false;
  }
  if (error instanceof SessionExpiredError) {
    return false;
  }
  if (error instanceof ApiError) {
    return (
      error.status >= 500 ||
      error.status === 429 ||
      (error.status === 404 && error.messageKey === SESSION_NOT_FOUND)
    );
  }
  return true;
}

/** 1 s, 2 s, 4 s … capped at a minute. */
export function feedbackRetryDelay(failureCount: number): number {
  return Math.min(MAX_DELAY_MS, 1000 * 2 ** Math.max(0, failureCount - 1));
}

export function feedbackMutationDefaults(api: AuthorizedFetcher) {
  const def = sessionFeedbackMutation(api);
  return {
    mutationFn: (variables: FeedbackVariables) => def.mutationFn(variables),
    onError: (error: unknown) => {
      console.error("[workout] feedback was not delivered", error);
    },
    retry: shouldRetryFeedback,
    retryDelay: feedbackRetryDelay,
  };
}

/** For `data/mutations.ts` (see the header): one line there. */
export function registerFeedbackMutationDefaults(
  client: Pick<QueryClient, "setMutationDefaults">,
  api: AuthorizedFetcher
): void {
  client.setMutationDefaults(
    [...FEEDBACK_MUTATION_KEY],
    feedbackMutationDefaults(api)
  );
}
